import React, { useMemo, useState } from "react";
import {
  Row,
  Col,
  Modal,
  Button,
  Select,
  Input,
  Radio,
  Space,
  Form,
  notification,
} from "antd";
import { RadioChangeEvent } from "antd";
import styles from "./Modals.module.scss";
import { useWindowSize } from "../../../hooks/useWindowSize";
import { useBillingHistoryCtx } from "../../../context/BillingHistoryContext";
import { refundTransactionByPaymentInstrumentId } from "../../../services/PaymentService/PaymentTransactionService";
import {
  ITransactionDetails,
  RefundReasons,
} from "../../../types/PaymentTransactionTypes";
import { useTripsCtx } from "../../../context/TripDetailsContext";
import { usePlanDetailsCtx } from "../../../context/PlanDetailsContext";
import { usePersonSummaryCtx } from "../../../context/PersonSummaryContext";
import {
  EmailNotificationValues,
  TriggerEventId,
} from "../../../types/EmailNotificationType";
import EmailSubscriptionRefund from "../../../helpers/EmailSubscriptionRefund";
import EmailTripRefund from "../../../helpers/EmailTripRefund";
import {
  greenCircledCheckmark,
  selectDropdownArrowIcon,
} from "../../../assets/images";

type Props = {
  visible: boolean;
  setVisibility: Function;
  transaction: ITransactionDetails;
};

const RefundTransactionModal: React.FC<Props> = ({
  visible,
  setVisibility,
  transaction,
}) => {
  const [selectedReasonID, setSelectedReasonID] = useState<number>(0);
  const [value, setValue] = useState<string>("");
  const [shouldDisableButton, setShouldDisableButton] = useState<boolean>(true);

  const { wandaOrgId } = useTripsCtx();
  const { tripDetailsInfo } = usePlanDetailsCtx();
  const { caregiverSummaryInfo } = usePersonSummaryCtx();

  const { width } = useWindowSize();

  const [form] = Form.useForm();

  const onChange = (e: RadioChangeEvent) => setValue(e.target.value);

  const { TextArea } = Input;

  const {
    refundTransactionReasonsCtx,
    billingHistoryForceRefetchCtx,
  } = useBillingHistoryCtx();
  const [, setForceRefetch] = billingHistoryForceRefetchCtx;
  const [refundTransactionReasons] = refundTransactionReasonsCtx;

  const otherReasonID = useMemo(
    () =>
      refundTransactionReasons?.find(
        (reason: RefundReasons) => reason.label === "Other"
      )?.value,
    [refundTransactionReasons]
  );

  const handleChange = (value: number) => setSelectedReasonID(value);

  const resetFormAndSetDefaults = () => {
    setVisibility(false);
    setValue("");
    setSelectedReasonID(0);
    setShouldDisableButton(true);
    form.resetFields();
  };

  const handleCancel = () => resetFormAndSetDefaults();

  const handleConfirmRefund = async () => {
    try {
      const {
        refundType,
        partialAmount,
        refundReason: reasonId,
        notes,
      } = form.getFieldsValue();
      const amount =
        refundType === "partialRefund"
          ? Number(partialAmount)
          : transaction?.amount;
      const isRefundSubscription =
        transaction?.paymentTransactionTypeName === "Subscription";
      const isTripServiceRefund =
        transaction?.paymentTransactionTypeName === "Service";

      await refundTransactionByPaymentInstrumentId(
        transaction?.paymentTransactionId,
        {
          amount,
          reasonId,
          notes,
        }
      );

      notification.success({
        message: `Refund requested for Transaction ${transaction?.paymentTransactionId}`,
        icon: <img src={greenCircledCheckmark} />,
      });

      setForceRefetch((prev: boolean) => !prev);

      isRefundSubscription &&
        EmailSubscriptionRefund({
          eventId: TriggerEventId.SubscriptionRefund,
          personId: transaction?.customerPersonId,
          organizationId: wandaOrgId.privatePayId,
          caregiverName: `${caregiverSummaryInfo.firstName} ${caregiverSummaryInfo.lastName}`,
          refundAmount: amount.toString(),
          ccLast4Digits: transaction?.paymentInstrumentName?.slice(-4),
          subscriptionType: EmailNotificationValues.PlanName,
          subscriptionDate: transaction?.createdOn,
          subscriptionPrice: tripDetailsInfo.wandaSubscriptionCost,
          transactionId: transaction?.paymentTransactionId?.toString(),
          customerServicePhone: tripDetailsInfo.wandaCustomerCareNumber,
        });

      isTripServiceRefund &&
        EmailTripRefund(
          {
            eventId: TriggerEventId.TripRefund,
            personId: transaction?.customerPersonId,
            serviceUserPersonId: transaction?.serviceUserPersonId,
            organizationId: wandaOrgId.privatePayId,
            caregiverName: `${caregiverSummaryInfo.firstName} ${caregiverSummaryInfo.lastName}`,
            serviceDate: transaction.createdOn,
            serviceType: transaction?.serviceType,
            customerServicePhone: tripDetailsInfo.wandaCustomerCareNumber,
            ccLast4Digits: transaction?.paymentInstrumentName?.slice(-4),
            refundAmount: amount.toString(),
            bookingId: transaction?.bookingId.toString(),
            services: [{ serviceId: transaction?.tripId?.toString() }],
            sourceSystemId: 0,
          },
          transaction.requestAssignmentId,
          transaction.serviceUserPersonId
        );

      resetFormAndSetDefaults();
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to Refund Transaction",
      });
    }
  };

  const checkEmptyFields = (
    values: Record<string, string | number>,
    excludeKeys: string[]
  ): boolean => {
    return Object.keys(values).some((key) => {
      if (excludeKeys.includes(key)) {
        return false;
      }
      const value = values[key];
      return value === undefined || value === "";
    });
  };

  const handleValuesChange = async () => {
    const values = form.getFieldsValue();
    const isNotesAdded = form.getFieldValue("notes");
    values.refundType === "fullRefund" &&
      values.partialAmount &&
      form.setFieldsValue({ partialAmount: "" });
    const isPartialAmountAdded = form.getFieldValue("partialAmount");
    let isEmptyFieldPresent;

    if (values.refundType === "partialRefund") {
      const excludeKeys =
        values.refundReason === otherReasonID ? [] : ["notes"];
      isEmptyFieldPresent = checkEmptyFields(values, excludeKeys);
    } else {
      const excludeKeys =
        values.refundReason === otherReasonID
          ? ["partialAmount"]
          : ["notes", "partialAmount"];
      isEmptyFieldPresent = checkEmptyFields(values, excludeKeys);
    }

    if (
      values.refundType === "partialRefund" &&
      !Number(isPartialAmountAdded)
    ) {
      setShouldDisableButton(true);
      return;
    }

    if (
      values.refundReason === otherReasonID &&
      (!isNotesAdded ||
        values?.notes?.startsWith(" ") ||
        values?.notes?.startsWith("\n"))
    ) {
      setShouldDisableButton(true);
      return;
    }

    if (isEmptyFieldPresent) {
      setShouldDisableButton(isEmptyFieldPresent);
      return;
    }

    try {
      await form.validateFields();
      setShouldDisableButton(false);
    } catch (error: any) {
      error.outOfDate ? handleValuesChange() : setShouldDisableButton(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement;

    if (
      e.key === "Enter" &&
      target.selectionStart === 0 &&
      target.selectionEnd === 0
    ) {
      e.preventDefault();
    }
  };

  return (
    <Modal
      visible={visible}
      className={styles.refundTransactionContainer}
      centered={width < 992}
      width={width < 992 ? "unset" : "566px"}
      footer={
        <Row justify="center" gutter={[15, 10]}>
          <Col>
            <Button className={styles.cancelBtn} onClick={handleCancel}>
              Cancel
            </Button>
          </Col>
          <Col>
            <Button
              className={`${styles.confirmRefundBtn} ${shouldDisableButton &&
                styles.disableOpacity}`}
              onClick={handleConfirmRefund}
              disabled={shouldDisableButton}
            >
              Confirm Refund
            </Button>
          </Col>
        </Row>
      }
    >
      <Form form={form} onValuesChange={handleValuesChange}>
        <Row justify="center">
          <Col span={24} className={styles.title}>
            Refund Transaction
          </Col>
        </Row>
        <Row>
          <Col span={24} className={styles.subTitle}>
            Refund Amount
          </Col>
          <Col span={24} className={styles.radioContainer}>
            <Form.Item name="refundType">
              <Radio.Group
                onChange={onChange}
                value={value}
                className={styles.radioSelectRefund}
              >
                <Space direction="vertical">
                  <Radio value="fullRefund">
                    <Row gutter={[0, 6]}>
                      <Col
                        span={24}
                        className={`${styles.option} ${
                          value === "fullRefund"
                            ? styles.selected
                            : styles.unselected
                        }`}
                      >
                        Full Refund
                      </Col>
                      {value === "fullRefund" && (
                        <Col span={24} className={styles.description}>
                          Refund Amount: ${transaction?.amount?.toFixed(2)}
                        </Col>
                      )}
                    </Row>
                  </Radio>
                  <Radio value="partialRefund">
                    <Row gutter={[0, 6]}>
                      <Col
                        span={24}
                        className={`${styles.option} ${
                          value === "partialRefund"
                            ? styles.selected
                            : styles.unselected
                        }`}
                      >
                        Partial Refund
                      </Col>
                    </Row>
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
            {value === "partialRefund" && (
              <Row className={styles.partialRefundContainer} gutter={[0, 6]}>
                <Col span={24} className={styles.description}>
                  Refund must be less than ${transaction?.amount.toFixed(2)}
                </Col>
                <Col className={styles.dollar}>
                  <span>$</span>
                </Col>
                <Col>
                  <Form.Item
                    name="partialAmount"
                    rules={[
                      () => ({
                        validator(_, value) {
                          if (value === "") {
                            return Promise.resolve();
                          }
                          if ([0, NaN].includes(Number(value))) {
                            return Promise.reject(new Error("Invalid Amount"));
                          }
                          if (value && Number(value) > transaction?.amount) {
                            return Promise.reject(new Error("Invalid Amount"));
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input
                      className={styles.partialRefundInput}
                      placeholder="0.00"
                      onChange={(event) => {
                        let value = event.target.value;
                        value = value.replace(/[^0-9.]+/, "");
                        value = value.replace(/(\..*)\./, "$1");
                        value = value.replace(/(\.\d{2})\d+/, "$1");

                        if (value.startsWith(".")) {
                          value = "0" + value;
                        }

                        form.setFieldsValue({ partialAmount: value });
                        form.validateFields(["partialAmount"]);
                      }}
                      onBlur={(event) => {
                        let value = event.target.value;
                        if (value && !value.includes(".")) {
                          value += ".00";
                        } else {
                          const splitValue = value.split(".");
                          if (splitValue.length === 2) {
                            value =
                              splitValue[0] +
                              "." +
                              splitValue[1].padEnd(2, "0");
                          }
                        }
                        form.setFieldsValue({ partialAmount: value });
                        form.validateFields(["partialAmount"]);
                      }}
                      onPaste={(e) => e.preventDefault()}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Col>
          <Col span={24} className={styles.subTitle}>
            Refund Reason
          </Col>
          <Col span={24}>
            <Form.Item name="refundReason">
              <Select
                placeholder="Select a reason"
                className={styles.selectRefundReason}
                onChange={handleChange}
                options={refundTransactionReasons}
                suffixIcon={
                  <img
                    src={selectDropdownArrowIcon}
                    alt="select_reason_dropdown"
                  />
                }
              />
            </Form.Item>
          </Col>
          {selectedReasonID === otherReasonID && (
            <>
              <Col span={24} className={styles.subTitle}>
                Enter comments
              </Col>
              <Col span={24}>
                <Form.Item name="notes">
                  <TextArea
                    maxLength={150}
                    className={styles.commentTextBox}
                    rows={4}
                    placeholder="Enter comments"
                    onChange={(e) => {
                      let value = e.target.value;
                      value = value.trimStart();
                      form.setFieldsValue({ notes: value });
                    }}
                    onKeyDown={handleKeyDown}
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      </Form>
    </Modal>
  );
};
export default RefundTransactionModal;
